import { get, post } from "../client";

/**
 * App-entry promo popup — the super-admin-controlled campaign the Admin-App
 * shows on launch, ported to the dashboard so web users see campaigns too (and
 * so the super-admin's numbers stop under-reporting mobile-only traffic).
 *
 * Backend: Backend/src/controllers/entryPopup.controller.js
 *   GET  /v1/entry-popup         — PUBLIC (no auth). Fails soft server-side and
 *                                  returns the disabled default on any error.
 *   POST /v1/entry-popup/events  — businessAuth. Batch telemetry. The business
 *                                  identity comes from the JWT, never the body.
 *
 * BASE_URL already carries the /v1 prefix, so the paths here are bare.
 */

/**
 * Shape ONE analytics row for the wire.
 *
 * Field-name note (verified against the backend, not assumed):
 *   • The live `production` controller reads `const type = e.type || e.eventType`
 *     and persists `type` — so `type` is the canonical name and what new code
 *     should send.
 *   • The in-flight feature branch (feature/hyp-phase2) still validates ONLY
 *     `eventType` and drops any row without it — silently, with a 200 back.
 * Sending BOTH names costs nothing, is ignored by whichever server does not
 * care, and is the difference between the dashboard being counted and the web
 * events vanishing exactly the way the mobile ones once did.
 */
const shapeEvent = (e) => {
  const type = (e && e.type) || "";
  return {
    type, // canonical
    eventType: type, // compatibility mirror — see note above
    campaignVersion: e.campaignVersion,
    slideIndex: e.slideIndex === undefined ? null : e.slideIndex,
    actionType: e.actionType || "",
    ts: new Date().toISOString(),
  };
};

export default {
  // Campaign config. Throws like every other service call — the caller decides
  // what a failure means (for the popup: render nothing).
  get_popup: async () => get("/entry-popup"),

  /**
   * Fire-and-forget telemetry. Deliberately NOT async and deliberately returns
   * nothing: there is no promise for the UI to await, no retry, no queue, and
   * every failure (offline, 401, 500, a thrown interceptor) is swallowed here
   * so telemetry can never surface as a broken screen. A lost event is always
   * preferable to a degraded UX.
   *
   * @param {Array<{type:string, campaignVersion:number, slideIndex?:number|null,
   *                actionType?:string}>} events
   */
  send_events: (events) => {
    try {
      if (!Array.isArray(events) || events.length === 0) return;
      const rows = events.filter(Boolean).map(shapeEvent);
      if (rows.length === 0) return;
      const p = post("/entry-popup/events", { events: rows });
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_e) {
      // never let telemetry throw into the caller
    }
  },
};
